// Redux State Synchronization Middleware
// Ensures data consistency across all Redux slices

import { Middleware, MiddlewareAPI, Dispatch, AnyAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import DataConsistencyService from '../services/DataConsistencyService';

// Action types that trigger state synchronization
const SYNC_TRIGGERS = [
  'employees/setEmployees',
  'employees/updateEmployee',
  'filters/setFilter',
  'filters/clearAllFilters',
  'dashboard/setMetrics',
  'dashboard/updateKPIs',
  'auth/loginSuccess',
  'auth/updateUserRole'
];

// Debounce cache updates to prevent performance issues
let syncDebounceTimer: number | null = null;
const SYNC_DEBOUNCE_MS = 500;

// State validation rules
const STATE_VALIDATION_RULES = {
  employees: {
    maxCount: DataConsistencyService.getTotalEmployees(),
    requiredFields: ['id', 'name', 'email', 'department', 'position']
  },
  metrics: {
    percentageFields: ['attritionRate', 'engagementScore', 'diversityScore'],
    countFields: ['totalEmployees', 'activeEmployees', 'openPositions']
  },
  dates: {
    format: 'ISO',
    maxFuture: 365, // days
    maxPast: 3650 // days (10 years)
  }
};

/**
 * Validates employee data consistency
 */
function validateEmployeeData(employees: any[]): boolean {
  if (!Array.isArray(employees)) return false;
  
  // Check count doesn't exceed total
  if (employees.length > STATE_VALIDATION_RULES.employees.maxCount) {
    console.warn(`Employee count ${employees.length} exceeds maximum ${STATE_VALIDATION_RULES.employees.maxCount}`);
    return false;
  }
  
  // Validate required fields
  return employees.every(emp => 
    STATE_VALIDATION_RULES.employees.requiredFields.every(field => 
      emp.hasOwnProperty(field) && emp[field] !== null && emp[field] !== undefined
    )
  );
}

/**
 * Validates metric data consistency
 */
function validateMetrics(metrics: any): boolean {
  if (!metrics || typeof metrics !== 'object') return false;
  
  // Validate percentage fields (0-100)
  for (const field of STATE_VALIDATION_RULES.metrics.percentageFields) {
    if (metrics[field] !== undefined) {
      const value = parseFloat(metrics[field]);
      if (isNaN(value) || value < 0 || value > 100) {
        console.warn(`Invalid percentage value for ${field}: ${metrics[field]}`);
        return false;
      }
    }
  }
  
  // Validate count fields (non-negative)
  for (const field of STATE_VALIDATION_RULES.metrics.countFields) {
    if (metrics[field] !== undefined) {
      const value = parseInt(metrics[field]);
      if (isNaN(value) || value < 0) {
        console.warn(`Invalid count value for ${field}: ${metrics[field]}`);
        return false;
      }
      
      // Special validation for totalEmployees
      if (field === 'totalEmployees' && value !== DataConsistencyService.getTotalEmployees()) {
        console.warn(`Total employees mismatch: ${value} vs ${DataConsistencyService.getTotalEmployees()}`);
      }
    }
  }
  
  return true;
}

/**
 * Synchronizes related state slices when one changes
 */
function synchronizeState(store: MiddlewareAPI<Dispatch<AnyAction>, RootState>, action: AnyAction) {
  const state = store.getState();
  
  // Clear existing debounce timer
  if (syncDebounceTimer) {
    clearTimeout(syncDebounceTimer);
  }
  
  // Debounce synchronization
  syncDebounceTimer = window.setTimeout(() => {
    performStateSynchronization(store, action, state);
  }, SYNC_DEBOUNCE_MS);
}

/**
 * Performs the actual state synchronization
 */
function performStateSynchronization(
  store: MiddlewareAPI<Dispatch<AnyAction>, RootState>, 
  action: AnyAction,
  state: RootState
) {
  // Synchronize based on action type
  switch (action.type) {
    case 'employees/setEmployees':
      syncEmployeeChanges(store, state);
      break;
      
    case 'filters/setFilter':
    case 'filters/clearAllFilters':
      syncFilterChanges(store, state);
      break;
      
    case 'dashboard/setMetrics':
    case 'dashboard/updateKPIs':
      syncMetricChanges(store, state);
      break;
      
    case 'auth/loginSuccess':
    case 'auth/updateUserRole':
      syncAuthChanges(store, state);
      break;
      
    default:
      break;
  }
  
  // Update cache with latest state
  updateStateCache(state);
}

/**
 * Synchronizes employee-related state changes
 */
function syncEmployeeChanges(store: MiddlewareAPI<Dispatch<AnyAction>, RootState>, state: RootState) {
  const { employees } = state.employees;
  
  // Validate employee data
  if (!validateEmployeeData(employees)) {
    console.error('Invalid employee data detected during synchronization');
    return;
  }
  
  // Update dashboard metrics based on employee data
  const metrics = calculateMetricsFromEmployees(employees);
  store.dispatch({ type: 'dashboard/updateMetrics', payload: metrics });
  
  // Update filter options based on available data
  const filterOptions = extractFilterOptions(employees);
  store.dispatch({ type: 'filters/updateFilterOptions', payload: filterOptions });
}

/**
 * Synchronizes filter-related state changes
 */
function syncFilterChanges(store: MiddlewareAPI<Dispatch<AnyAction>, RootState>, state: RootState) {
  const { filters } = state.filters;
  
  // Re-filter employees based on current filters
  const filteredEmployees = applyFiltersToEmployees(state.employees.employees, filters);
  
  // Update filtered count in dashboard
  store.dispatch({ 
    type: 'dashboard/updateFilteredCount', 
    payload: filteredEmployees.length 
  });
}

/**
 * Synchronizes metric-related state changes
 */
function syncMetricChanges(store: MiddlewareAPI<Dispatch<AnyAction>, RootState>, state: RootState) {
  const { metrics } = state.dashboard;
  
  // Validate metrics
  if (!validateMetrics(metrics)) {
    console.error('Invalid metrics detected during synchronization');
    return;
  }
  
  // Ensure consistent total employee count
  if (metrics && metrics.totalEmployees !== DataConsistencyService.getTotalEmployees()) {
    store.dispatch({
      type: 'dashboard/updateMetrics',
      payload: {
        ...metrics,
        totalEmployees: DataConsistencyService.getTotalEmployees()
      }
    });
  }
}

/**
 * Synchronizes auth-related state changes
 */
function syncAuthChanges(store: MiddlewareAPI<Dispatch<AnyAction>, RootState>, state: RootState) {
  const { user } = state.auth;
  
  if (!user) return;
  
  // Apply role-based data filtering
  const roleConfig = getRoleConfiguration(user.role);
  
  // Update visible data based on role
  store.dispatch({
    type: 'employees/applyRoleFilter',
    payload: roleConfig
  });
  
  // Update dashboard configuration based on role
  store.dispatch({
    type: 'dashboard/applyRoleConfig',
    payload: roleConfig
  });
}

/**
 * Helper function to calculate metrics from employee data
 */
function calculateMetricsFromEmployees(employees: any[]) {
  const total = employees.length;
  const atRisk = employees.filter(e => e.attritionRisk > 70).length;
  const engaged = employees.filter(e => e.engagementScore >= 7).length;
  
  return {
    totalEmployees: total,
    attritionRate: total > 0 ? (atRisk / total) * 100 : 0,
    engagementScore: total > 0 ? (engaged / total) * 100 : 0,
    lastUpdated: new Date().toISOString()
  };
}

/**
 * Helper function to extract filter options from employee data
 */
function extractFilterOptions(employees: any[]) {
  const departmentsSet = new Set(employees.map(e => e.department));
  const locationsSet = new Set(employees.map(e => e.location));
  const levelsSet = new Set(employees.map(e => e.level));
  
  const departments = Array.from(departmentsSet);
  const locations = Array.from(locationsSet);
  const levels = Array.from(levelsSet);
  
  return {
    departments: departments.sort(),
    locations: locations.sort(),
    levels: levels.sort()
  };
}

/**
 * Helper function to apply filters to employee data
 */
function applyFiltersToEmployees(employees: any[], filters: any) {
  return employees.filter(employee => {
    if (filters.department && employee.department !== filters.department) return false;
    if (filters.location && employee.location !== filters.location) return false;
    if (filters.level && employee.level !== filters.level) return false;
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const matchesSearch = 
        employee.name?.toLowerCase().includes(searchLower) ||
        employee.email?.toLowerCase().includes(searchLower) ||
        employee.position?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }
    return true;
  });
}

/**
 * Helper function to get role-based configuration
 */
function getRoleConfiguration(role: string) {
  const configs: Record<string, any> = {
    HR_LEADER: {
      maxEmployeesVisible: -1, // All employees
      sensitiveDataAccess: true,
      aiInsightsAccess: true
    },
    MANAGER: {
      maxEmployeesVisible: 500, // Limited view
      sensitiveDataAccess: false,
      aiInsightsAccess: true
    },
    RECRUITER: {
      maxEmployeesVisible: 0, // No employee access
      sensitiveDataAccess: false,
      aiInsightsAccess: false
    },
    EMPLOYEE: {
      maxEmployeesVisible: 0, // Only self
      sensitiveDataAccess: false,
      aiInsightsAccess: false
    }
  };
  
  return configs[role] || configs.EMPLOYEE;
}

/**
 * Updates the state cache for performance optimization
 */
function updateStateCache(state: RootState) {
  // Cache frequently accessed data
  DataConsistencyService.cache.set('employeeCount', state.employees.employees.length);
  DataConsistencyService.cache.set('currentFilters', state.filters.filters);
  DataConsistencyService.cache.set('dashboardMetrics', state.dashboard.metrics);
  DataConsistencyService.cache.set('lastSync', new Date().toISOString());
}

// Helper to determine if action should be logged
const shouldLogAction = (actionType: string): boolean => {
  // Only log important actions, not routine updates
  const importantActions = [
    'auth/loginSuccess',
    'auth/logout',
    'employees/setEmployees',
    'dashboard/setMetrics'
  ];
  
  return importantActions.includes(actionType);
};

/**
 * Main middleware function
 */
const stateSyncMiddleware = (store: any) => (next: any) => (action: any) => {
  // Execute the action first
  const result = next(action);
  
  // Check if this action should trigger synchronization
  if (SYNC_TRIGGERS.includes(action.type)) {
    synchronizeState(store, action);
  }
  
  // Reduced logging in development - only log important actions
  if (process.env.NODE_ENV === 'development' && shouldLogAction(action.type)) {
    console.log(`[State Sync] Action: ${action.type}`, {
      timestamp: new Date().toISOString()
    });
  }
  
  return result;
};

export default stateSyncMiddleware;

// Export utility functions for testing
export {
  validateEmployeeData,
  validateMetrics,
  calculateMetricsFromEmployees,
  extractFilterOptions,
  applyFiltersToEmployees,
  getRoleConfiguration
};